import { alpha, styled } from '@mui/material/styles';
import { Grid, gridClasses, List, ListItem, ListSubheader, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import PaperBackground from 'app/components/PaperBackground';

export const REGULATIONS_FIELDS_PANEL_BODY_ID = 'regulations_fields_panel_body_id';

const disabledStyles = (theme: any) => ({
  color: theme.palette.secondary.dark,
});

const highlightedStyles = (theme: any) => ({
  fontWeight: 650,
  color: theme.palette.common.black,
});

export const GridColumnStyled = styled(Grid, {
  name: 'GridColumnStyled',
  shouldForwardProp: (propName: string) =>
    !['hasDividerLeft', 'hasDividerRight', 'hasDividerTop', 'hasDividerBottom'].includes(propName),
})<{ hasDividerLeft: boolean; hasDividerRight: boolean; hasDividerTop: boolean; hasDividerBottom: boolean }>(
  ({ theme, hasDividerLeft, hasDividerRight, hasDividerTop, hasDividerBottom }) => ({
    ...(hasDividerLeft
      ? {
          borderLeftColor: theme.palette.secondary.main,
          borderLeftWidth: '1px',
          borderLeftStyle: 'solid',
          [`&.${gridClasses.item}`]: {
            paddingLeft: theme.spacing(3),
          },
        }
      : {}),
    ...(hasDividerRight
      ? {
          [`&.${gridClasses.item}`]: {
            paddingRight: theme.spacing(3),
          },
        }
      : {}),
    ...(hasDividerTop
      ? {
          borderTopColor: theme.palette.secondary.main,
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          [`&.${gridClasses.item}`]: {
            paddingTop: theme.spacing(3),
          },
        }
      : {}),
    ...(hasDividerBottom
      ? {
          [`&.${gridClasses.item}`]: {
            paddingBottom: theme.spacing(3),
          },
        }
      : {}),
  }),
);

export const TypographyColumnTitleStyled = styled(Typography, {
  name: 'TypographyColumnTitleStyled',
  shouldForwardProp: (propName: string) => !['withMarginTop', 'withMarginBottom'].includes(propName),
})<{ withMarginTop: boolean; withMarginBottom: boolean }>(({ theme, withMarginTop, withMarginBottom }) => ({
  ...(withMarginTop ? { marginTop: theme.spacing(1.25) } : {}),
  ...(withMarginBottom ? { marginBottom: theme.spacing(1.25) } : {}),
}));

export const TypographyColumnSubTitleStyled = styled(Typography, {
  name: 'TypographyColumnSubTitleStyled',
  shouldForwardProp: (propName: string) => !['withMarginTop'].includes(propName),
})<{ withMarginTop: boolean }>(({ theme, withMarginTop }) => ({
  marginBottom: theme.spacing(1.25),
  ...(withMarginTop ? { marginTop: theme.spacing(1.25) } : {}),
}));

export const GridHeaderStyled = styled(Grid, {
  name: 'GridHeaderStyled',
})(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
  margin: `0 -${theme.spacing(1)}px 0 -${theme.spacing(1)}px`,
  width: `calc(100% + ${theme.spacing(2)}px)`,
}));

export const DivHeaderContentStyled = styled('div', {
  name: 'DivHeaderContentStyled',
  shouldForwardProp: (propName: string) => !['withIcon'].includes(propName),
})<{ withIcon: boolean }>(({ theme, withIcon }) => ({
  paddingLeft: theme.spacing(1),
  ...(withIcon
    ? {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& svg': {
          fontSize: '1rem',
        },
      }
    : {}),
}));

export const PaperBackgroundStyled = styled(PaperBackground, {
  name: 'PaperBackgroundStyled',
})(() => ({}));

export const GridActionsStyled = styled(Grid, {
  name: 'GridActionsStyled',
})(({ theme }) => ({
  borderTop: `1px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
  display: 'flex',
  justifyContent: 'flex-end',
  margin: `0 -${theme.spacing(1)}px -${theme.spacing(1)}px -${theme.spacing(1)}px`,
}));

export const SpanFieldLabelStyled = styled('span', {
  name: 'SpanFieldLabelStyled',
  shouldForwardProp: (propName: string) => !['disabled', 'highlighted'].includes(propName),
})<{ disabled: boolean; highlighted: boolean }>(({ theme, disabled, highlighted }) => ({
  ...theme.typography.h6,
  fontWeight: 500,
  lineHeight: '24px',
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingRight: theme.spacing(0.5),
  overflowWrap: 'break-word',
  ...(disabled ? disabledStyles(theme) : {}),
  ...(highlighted ? highlightedStyles(theme) : {}),
}));

export const SpanFiledValueStyled = styled('span', {
  name: 'SpanFiledValueStyled',
  shouldForwardProp: (propName: string) => !['disabled', 'highlighted'].includes(propName),
})<{ disabled: boolean; highlighted: boolean }>(({ theme, disabled, highlighted }) => ({
  ...theme.typography.h6,
  lineHeight: '24px',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  overflowWrap: 'break-word',
  ...(disabled ? disabledStyles(theme) : {}),
  ...(highlighted ? highlightedStyles(theme) : {}),
}));

export const ListItemStyled = styled(ListItem, {
  name: 'ListItemStyled',
})(({ theme }) => ({
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  paddingLeft: 0,
  paddingRight: 0,
}));

export const ListSubheaderStyled = styled(ListSubheader, {
  name: 'ListSubheaderStyled',
})(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0.5, 0),
  lineHeight: 'unset',
  backgroundColor: theme.palette.primary.light,
}));

export const FontAwesomeIconStyled = styled(FontAwesomeIcon, {
  name: 'FontAwesomeIconStyled',
})(() => ({
  fontSize: '0.8rem',
}));

export const ListStyled = styled(List, {
  name: 'ListStyled',
})(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.light,
  position: 'relative',
  padding: 0,
}));
